/**
 * Hora local del servidor SHC (IANA TZ), independiente del TZ del navegador.
 * Sync vía GET /api/server-time.
 */

const SYNC_INTERVAL_MS = 5 * 60 * 1000;
/** Si el fetch tardó más que esto, la página estuvo suspendida: el unix_ms ya no sirve. */
const MAX_TRUSTED_RTT_MS = 8_000;
/** Salto de reloj (pantalla apagada / JS congelado) a partir del cual se fuerza un sync. */
const WAKE_GAP_MS = 15_000;
const OFFSET_CORRECT_MS = 2_000;
const TZ_CACHE_KEY = 'shc_server_tz_v1';

let sharedTz = 'UTC';
let sharedOffsetMs = 0;
let lastSyncAt = 0;
let generation = 0;
let running = false;
let queued = false;
let retryTimer: number | null = null;
let retryDelay = 2_000;
let lastBeat = 0;

export type ServerTimeEvent = { offsetCorrected: boolean };

const listeners = new Set<(ev: ServerTimeEvent) => void>();

/** Avisa cuando un sync de confianza termina. `offsetCorrected` = el reloj se movió >2s. */
export function subscribeServerTime(listener: (ev: ServerTimeEvent) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(offsetCorrected: boolean): void {
  const ev: ServerTimeEvent = { offsetCorrected };
  listeners.forEach((fn) => {
    try {
      fn(ev);
    } catch {
      /* un listener no debe romper el sync */
    }
  });
}

try {
  const cached = typeof localStorage !== 'undefined' ? localStorage.getItem(TZ_CACHE_KEY) : null;
  if (cached && cached.trim()) sharedTz = cached.trim();
} catch {
  /* private mode */
}

function part(
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes,
): string {
  return parts.find((p) => p.type === type)?.value ?? '00';
}

function scheduleRetry(): void {
  if (retryTimer != null) return;
  const delay = retryDelay;
  retryDelay = Math.min(retryDelay * 2, 60_000);
  retryTimer = window.setTimeout(() => {
    retryTimer = null;
    forceServerTimeSync();
  }, delay);
}

async function syncOnce(gen: number): Promise<void> {
  const startedAt = Date.now();
  try {
    const res = await fetch('/api/server-time', {
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    });
    if (gen !== generation) return;
    const finishedAt = Date.now();
    // La respuesta llegó después de un freeze: unix_ms es de antes y el offset quedaría atrasado.
    if (finishedAt - startedAt > MAX_TRUSTED_RTT_MS) {
      scheduleRetry();
      return;
    }
    if (!res.ok) {
      if (res.status >= 500) scheduleRetry();
      return;
    }
    // Un 200 cacheado (service worker) trae Date y unix_ms de hace rato, con RTT corto.
    const headerMs = Date.parse(res.headers.get('date') || '');
    if (Number.isFinite(headerMs) && Math.abs(headerMs - finishedAt) > MAX_TRUSTED_RTT_MS) {
      scheduleRetry();
      return;
    }
    const data = (await res.json()) as {
      unix_ms?: number;
      timezone?: string;
    };
    if (gen !== generation) return;
    if (
      Number.isFinite(headerMs)
      && typeof data.unix_ms === 'number'
      && Math.abs(headerMs - data.unix_ms) > MAX_TRUSTED_RTT_MS
    ) {
      scheduleRetry();
      return;
    }

    let corrected = false;
    if (typeof data.unix_ms === 'number' && Number.isFinite(data.unix_ms)) {
      const next = data.unix_ms - finishedAt;
      if (Math.abs(next - sharedOffsetMs) > OFFSET_CORRECT_MS) corrected = true;
      sharedOffsetMs = next;
    }
    if (typeof data.timezone === 'string' && data.timezone.trim()) {
      const tz = data.timezone.trim();
      if (tz !== sharedTz) corrected = true;
      sharedTz = tz;
      try {
        localStorage.setItem(TZ_CACHE_KEY, sharedTz);
      } catch {
        /* ignore */
      }
    }
    lastSyncAt = finishedAt;
    retryDelay = 2_000;
    notify(corrected);
  } catch {
    if (gen === generation) scheduleRetry();
  }
}

function pump(): void {
  if (running) return;
  running = true;
  const step = (): void => {
    if (!queued) {
      running = false;
      return;
    }
    queued = false;
    const gen = generation;
    void syncOnce(gen).finally(() => {
      if (queued || gen !== generation) {
        queued = true;
        step();
      } else {
        running = false;
      }
    });
  };
  step();
}

/** Sync inmediato, aunque el anterior tenga menos de 5 minutos. Descarta uno en vuelo. */
export function forceServerTimeSync(): void {
  generation += 1;
  queued = true;
  if (retryTimer != null) {
    window.clearTimeout(retryTimer);
    retryTimer = null;
  }
  pump();
}

/** Dispara sync si hace falta (idempotente). */
export function ensureServerTimeSync(): void {
  const now = Date.now();
  if (running || queued) return;
  if (lastSyncAt === 0 || now - lastSyncAt > SYNC_INTERVAL_MS) {
    forceServerTimeSync();
  }
}

function onPossibleWake(): void {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  const now = Date.now();
  const gap = lastBeat === 0 ? 0 : now - lastBeat;
  lastBeat = now;
  if (gap > WAKE_GAP_MS || lastSyncAt === 0) {
    forceServerTimeSync();
  }
}

// Arranque temprano (charts pueden formatear antes del primer hook).
if (typeof window !== 'undefined') {
  lastBeat = Date.now();
  ensureServerTimeSync();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') onPossibleWake();
  });
  window.addEventListener('pageshow', (ev: PageTransitionEvent) => {
    if (ev.persisted) onPossibleWake();
  });
  window.addEventListener('online', () => {
    lastBeat = Date.now();
    forceServerTimeSync();
  });
  window.addEventListener('focus', () => onPossibleWake());
  // Si el intervalo de 1s del reloj murió durante el freeze, este late-tick igual re-sincroniza.
  window.setInterval(() => {
    const now = Date.now();
    const gap = now - lastBeat;
    lastBeat = now;
    if (gap > WAKE_GAP_MS) forceServerTimeSync();
    else ensureServerTimeSync();
  }, 5_000);
}

export function getServerAdjustedNow(): Date {
  return new Date(Date.now() + sharedOffsetMs);
}

export function getServerTimeZone(): string {
  return sharedTz;
}

export type ServerTimePattern =
  | 'HH:mm'
  | 'dd/MM'
  | 'dd/MM HH:mm'
  | 'dd/MM/yyyy'
  | 'dd.MM.yyyy'
  | 'yyyy-MM-dd'
  | 'dd/MM/yy HH:mm:ss';

export interface ServerZonedParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export function getServerZonedParts(
  date: Date = getServerAdjustedNow(),
  timeZone: string = getServerTimeZone(),
): ServerZonedParts {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  }).formatToParts(date);
  return {
    year: Number(part(parts, 'year')),
    month: Number(part(parts, 'month')),
    day: Number(part(parts, 'day')),
    hour: Number(part(parts, 'hour')),
    minute: Number(part(parts, 'minute')),
    second: Number(part(parts, 'second')),
  };
}

/** Offset ms tal que `Date.UTC(wall) - offset === instant`. */
function getTimeZoneOffsetMs(timeZone: string, date: Date): number {
  const p = getServerZonedParts(date, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - date.getTime();
}

/** Interpreta reloj de pared del servidor como instante UTC. month = 1..12 */
export function serverWallTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
  ms = 0,
  timeZone: string = getServerTimeZone(),
): Date {
  let utc = Date.UTC(year, month - 1, day, hour, minute, second, ms);
  const offset1 = getTimeZoneOffsetMs(timeZone, new Date(utc));
  utc -= offset1;
  const offset2 = getTimeZoneOffsetMs(timeZone, new Date(utc));
  if (offset2 !== offset1) {
    utc = Date.UTC(year, month - 1, day, hour, minute, second, ms) - offset2;
  }
  return new Date(utc);
}

export function startOfServerDay(date: Date = getServerAdjustedNow()): Date {
  const p = getServerZonedParts(date);
  return serverWallTimeToUtc(p.year, p.month, p.day, 0, 0, 0, 0);
}

export function endOfServerDay(date: Date = getServerAdjustedNow()): Date {
  const p = getServerZonedParts(date);
  return serverWallTimeToUtc(p.year, p.month, p.day, 23, 59, 59, 999);
}

/** 0=Domingo … 6=Sábado en TZ del servidor. */
export function getServerWeekday(date: Date = getServerAdjustedNow()): number {
  const wd = new Intl.DateTimeFormat('en-US', {
    timeZone: getServerTimeZone(),
    weekday: 'short',
  }).format(date);
  const map: Record<string, number> = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
  };
  return map[wd] ?? 0;
}

export function startOfServerWeekMonday(date: Date = getServerAdjustedNow()): Date {
  const sod = startOfServerDay(date);
  const wd = getServerWeekday(sod);
  const mondayOffset = wd === 0 ? 6 : wd - 1;
  return new Date(sod.getTime() - mondayOffset * 24 * 60 * 60 * 1000);
}

export function endOfServerWeekSunday(date: Date = getServerAdjustedNow()): Date {
  const start = startOfServerWeekMonday(date);
  return endOfServerDay(new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000));
}

export function startOfServerMonth(date: Date = getServerAdjustedNow()): Date {
  const p = getServerZonedParts(date);
  return serverWallTimeToUtc(p.year, p.month, 1, 0, 0, 0, 0);
}

export function endOfServerMonth(date: Date = getServerAdjustedNow()): Date {
  const p = getServerZonedParts(date);
  const last = new Date(Date.UTC(p.year, p.month, 0)).getUTCDate();
  return serverWallTimeToUtc(p.year, p.month, last, 23, 59, 59, 999);
}

export function startOfServerYear(date: Date = getServerAdjustedNow()): Date {
  const p = getServerZonedParts(date);
  return serverWallTimeToUtc(p.year, 1, 1, 0, 0, 0, 0);
}

export function endOfServerYear(date: Date = getServerAdjustedNow()): Date {
  const p = getServerZonedParts(date);
  return serverWallTimeToUtc(p.year, 12, 31, 23, 59, 59, 999);
}

export function addServerCalendarDays(date: Date, days: number): Date {
  const p = getServerZonedParts(date);
  // Usar UTC date math sobre el día civil del servidor
  const utcDay = Date.UTC(p.year, p.month - 1, p.day + days);
  const y = new Date(utcDay).getUTCFullYear();
  const m = new Date(utcDay).getUTCMonth() + 1;
  const d = new Date(utcDay).getUTCDate();
  return serverWallTimeToUtc(y, m, d, p.hour, p.minute, p.second, 0);
}

/** Parsea YYYY-MM-DD como 00:00 en TZ del servidor. */
export function parseYmdInServerTz(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd).trim());
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const da = parseInt(m[3], 10);
  const d = serverWallTimeToUtc(y, mo, da, 0, 0, 0, 0);
  const p = getServerZonedParts(d);
  if (p.year !== y || p.month !== mo || p.day !== da) return null;
  return d;
}

export function formatInServerTz(
  input: Date | string | number,
  pattern: ServerTimePattern,
  timeZone: string = getServerTimeZone(),
): string {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return String(input);

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: pattern === 'dd/MM/yy HH:mm:ss' ? '2-digit' : 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  }).formatToParts(date);

  const dd = part(parts, 'day');
  const mm = part(parts, 'month');
  const yyyy = part(parts, 'year');
  const HH = part(parts, 'hour');
  const mi = part(parts, 'minute');
  const ss = part(parts, 'second');

  switch (pattern) {
    case 'HH:mm':
      return `${HH}:${mi}`;
    case 'dd/MM':
      return `${dd}/${mm}`;
    case 'dd/MM HH:mm':
      return `${dd}/${mm} ${HH}:${mi}`;
    case 'dd/MM/yyyy':
      return `${dd}/${mm}/${yyyy}`;
    case 'dd.MM.yyyy':
      return `${dd}.${mm}.${yyyy}`;
    case 'yyyy-MM-dd':
      return `${yyyy}-${mm}-${dd}`;
    case 'dd/MM/yy HH:mm:ss':
      return `${dd}/${mm}/${yyyy} ${HH}:${mi}:${ss}`;
    default:
      return `${dd}/${mm}/${yyyy} ${HH}:${mi}:${ss}`;
  }
}

/** dd/MM/yy HH:mm:ss (compat / charts). */
export function formatServerCompactDateTime(
  date: Date,
  timeZone: string = getServerTimeZone(),
): string {
  return formatInServerTz(date, 'dd/MM/yy HH:mm:ss', timeZone);
}

const WEEKDAY_EN_TO_KEY: Record<string, string> = {
  monday: 'monday',
  tuesday: 'tuesday',
  wednesday: 'wednesday',
  thursday: 'thursday',
  friday: 'friday',
  saturday: 'saturday',
  sunday: 'sunday',
};

/**
 * Header de subpáginas: "Vie 07 Ago 26 · 18:49:55" (ES) / "Fri 07 Aug 26 · 18:49:55" (EN).
 * `t` debe resolver common.daysShort.* y common.monthsShort.1..12
 */
export function formatServerHeaderDateTime(
  date: Date,
  timeZone: string,
  t: (key: string, opts?: { defaultValue?: string }) => string,
): string {
  const z = getServerZonedParts(date, timeZone);
  const weekdayRaw = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'long',
  })
    .format(date)
    .toLowerCase();
  const dayKey = WEEKDAY_EN_TO_KEY[weekdayRaw] || 'monday';
  const dayShort = t(`common.daysShort.${dayKey}`, {
    defaultValue: dayKey.slice(0, 3),
  });
  const monthShort = t(`common.monthsShort.${z.month}`, {
    defaultValue: String(z.month).padStart(2, '0'),
  });
  const dd = String(z.day).padStart(2, '0');
  const yy = String(z.year).slice(-2);
  const HH = String(z.hour).padStart(2, '0');
  const mi = String(z.minute).padStart(2, '0');
  const ss = String(z.second).padStart(2, '0');
  return `${dayShort} ${dd} ${monthShort} ${yy} · ${HH}:${mi}:${ss}`;
}

/**
 * Formatea time_bucket de charts.
 * - Si ya viene preformateado por el backend (HH:mm / dd/MM …), se respeta.
 * - Si es ISO/timestamp, se formatea en TZ del servidor.
 */
export function formatChartTimeBucket(
  timeBucket: string | Date,
  pattern: ServerTimePattern,
): string {
  if (typeof timeBucket === 'string') {
    // Ya formateado por backend (HH:mm, dd/MM, dd/MM HH:mm)
    const looksPreformatted =
      /^(?:\d{2}\/\d{2}(?:\/\d{2,4})?(?:\s+\d{2}:\d{2})?|\d{2}:\d{2})$/.test(timeBucket.trim());
    if (looksPreformatted) return timeBucket;

    // Timestamp naive sin zona: interpretar como reloj de pared del servidor
    const naive = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(
      timeBucket.trim(),
    );
    if (naive) {
      const d = serverWallTimeToUtc(
        Number(naive[1]),
        Number(naive[2]),
        Number(naive[3]),
        Number(naive[4]),
        Number(naive[5]),
        Number(naive[6] || 0),
      );
      return formatInServerTz(d, pattern);
    }
  }
  try {
    const d = timeBucket instanceof Date ? timeBucket : new Date(timeBucket);
    if (Number.isNaN(d.getTime())) return String(timeBucket);
    return formatInServerTz(d, pattern);
  } catch {
    return String(timeBucket);
  }
}

export function pickChartTimePattern(
  periodHours: number,
  interval?: string,
): ServerTimePattern {
  if (periodHours > 6) {
    return interval === 'day' ? 'dd/MM' : 'dd/MM HH:mm';
  }
  if (
    interval === 'minute' ||
    interval === '5minutes' ||
    interval === '15minutes' ||
    interval === '30minutes'
  ) {
    return 'HH:mm';
  }
  return 'dd/MM HH:mm';
}

/**
 * Etiqueta de tooltip de charts: día (3 letras, i18n) + dd/MM/yy + HH:mm.
 * Ej.: "mié 06/08/25 14:30" / "Wed 06/08/25 14:30"
 */
export function formatChartTooltipTime(
  input: Date | string | number,
  localeLang: string = 'es',
  timeZone: string = getServerTimeZone(),
): string {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return String(input);

  const locale = localeLang.toLowerCase().startsWith('en') ? 'en-GB' : 'es-AR';
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  }).formatToParts(date);

  let eee = (part(parts, 'weekday') || '').replace(/\./g, '').trim();
  if (eee.length > 3) eee = eee.slice(0, 3);
  const dd = part(parts, 'day');
  const mm = part(parts, 'month');
  const yy = part(parts, 'year');
  const HH = part(parts, 'hour');
  const mi = part(parts, 'minute');
  return `${eee} ${dd}/${mm}/${yy} ${HH}:${mi}`;
}

/** Etiqueta corta de facturación en TZ servidor. */
export function formatServerDateLabel(
  date: Date,
  locale?: string,
): string {
  const lang = locale || (typeof navigator !== 'undefined' ? navigator.language : 'es-AR');
  return new Intl.DateTimeFormat(lang, {
    timeZone: getServerTimeZone(),
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  }).format(date);
}
